import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowsRightLeftIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';
import { fetchTenants, fetchDeviceInventory, fetchBpcTenants } from '../api/client';
import Pagination from './Pagination';
import {
  OSBadge,
  compareVals,
  applyColFilters,
  countActiveFilters,
  TableHead,
} from './coverage/coverageShared';
import { TIMEZONE_OPTIONS, TZ_STORAGE_KEY, getTzOffset, getTzLabel, formatTimestampShort } from './TimelineView';

// Local default for this page is 'Local' (browser-detected zone). If the user has set a
// preference on another page (e.g. Alerts → ET), honor it — TZ_STORAGE_KEY is shared so a
// single global TZ choice flows through. Pick stays adjustable via the picker on this page.
function getCoverageDefaultTz() {
  try {
    const stored = localStorage.getItem(TZ_STORAGE_KEY);
    if (stored && TIMEZONE_OPTIONS.find((tz) => tz.id === stored)) return stored;
  } catch { /* ignore */ }
  return 'Local';
}

// ── Column definitions ────────────────────────────────────────────────────────
//
// Columns colocate their render + CSV functions so the body and CSV export pipelines
// can iterate visibleCols dynamically (rather than hardcoding 11+ <td>s and a hardcoded
// CSV header). Render functions receive a `ctx` argument carrying the per-render-pass
// values that aren't directly on the row: { bpcLoaded, fmt(tz formatter), m365Stale,
// drmmStale, entraStale, intuneStale, bpcStale }. CSV functions receive { bpcLoaded }.
//
// `cellClass` and `cellTitle` let a column override the standard <td> styling without
// the caller needing to special-case it. Columns referenced by hoisted function
// declarations (PresenceCell, FaintPresenceDot, CountPill, OSBadge, presenceCsv) are
// all defined later in this file as `function …`, so they're safe to reference here.
const COMPARE_COLS = [
  {
    key: 'displayName', label: 'Device Name', w: 220, align: 'left', filterType: 'text',
    cellClass: 'px-3 py-2 font-mono truncate',
    cellTitle: (d) => d.displayName,
    render: (d) => d.displayName || '—',
    csv: (d) => d.displayName,
  },
  {
    key: 'hasM365', label: 'Seen By M365', w: 115, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      <PresenceCell
        value={d.hasM365}
        loaded={true}
        titleWhenPresent={`Seen By M365: lastSeen=${ctx.fmt(d.m365?.lastSeenUtc)}, OS=${d.m365?.osType || '—'}`}
        titleWhenAbsent={ctx.m365Stale
          ? `Stale: last seen ${ctx.fmt(d.m365?.lastSeenUtc)}`
          : 'Not seen in the M365 sign-in-derived inventory on this snapshot'}
      />
    ),
    csv: (d) => presenceCsv(d.hasM365),
  },
  {
    key: 'hasDrmm', label: 'DRMM', w: 85, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      <PresenceCell
        value={d.hasDrmm}
        loaded={true}
        titleWhenPresent={`DRMM: lastSeen=${ctx.fmt(d.drmm?.lastSeen)}, OS=${d.drmm?.osCommonName || '—'}`}
        titleWhenAbsent={ctx.drmmStale
          ? `Stale: last seen ${ctx.fmt(d.drmm?.lastSeen)}`
          : 'Missing from DRMM'}
        naTitle={d.naDrmmBpc ? `DRMM agents do not run on ${d.os} devices` : undefined}
      />
    ),
    csv: (d) => presenceCsv(d.hasDrmm),
  },
  {
    key: 'hasEntraRegistered', label: 'Entra Registered', w: 130, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      d.hasEntraJoined
        ? <FaintPresenceDot title={`Implicit — covered by Joined (${d.entra?.trustType}), lastSignIn=${ctx.fmt(d.entra?.approximateLastSignInDateTime)}`} />
        : <PresenceCell
            value={d.hasEntraRegistered}
            loaded={true}
            titleWhenPresent={`Workplace: lastSignIn=${ctx.fmt(d.entra?.approximateLastSignInDateTime)}`}
            titleWhenAbsent={d.inventoryEntraRegistered && ctx.entraStale
              ? `Stale: last sign-in ${ctx.fmt(d.entra?.approximateLastSignInDateTime)}`
              : (d.entra ? `trustType=${d.entra.trustType || '(none)'}, lastSignIn=${ctx.fmt(d.entra?.approximateLastSignInDateTime)}` : 'Not in Entra directory')}
          />
    ),
    csv: (d) => presenceCsv(d.hasEntraRegistered),
  },
  {
    key: 'hasEntraJoined', label: 'Entra Joined', w: 110, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      <PresenceCell
        value={d.hasEntraJoined}
        loaded={true}
        titleWhenPresent={`${d.entra?.trustType}: lastSignIn=${ctx.fmt(d.entra?.approximateLastSignInDateTime)}`}
        titleWhenAbsent={d.inventoryEntraJoined && ctx.entraStale
          ? `Stale: last sign-in ${ctx.fmt(d.entra?.approximateLastSignInDateTime)}`
          : (d.entra ? `trustType=${d.entra.trustType || '(none)'}, lastSignIn=${ctx.fmt(d.entra?.approximateLastSignInDateTime)}` : 'Not in Entra directory')}
      />
    ),
    csv: (d) => presenceCsv(d.hasEntraJoined),
  },
  {
    key: 'hasIntune', label: 'Intune', w: 85, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      <PresenceCell
        value={d.hasIntune}
        loaded={true}
        titleWhenPresent={`Intune: compliance=${d.intune?.complianceState || '—'}, lastSync=${ctx.fmt(d.intune?.lastSyncDateTime)}`}
        titleWhenAbsent={ctx.intuneStale
          ? `Stale: last sync ${ctx.fmt(d.intune?.lastSyncDateTime)}`
          : 'Not enrolled in Intune (or tenant not yet on Intune re-consent)'}
      />
    ),
    csv: (d) => presenceCsv(d.hasIntune),
  },
  {
    key: 'hasBpc', label: 'BPC', w: 85, align: 'center', filterType: 'bool',
    cellClass: 'px-3 py-2 text-center',
    render: (d, ctx) => (
      <PresenceCell
        value={d.hasBpc}
        loaded={ctx.bpcLoaded}
        titleWhenPresent={`BPC: lastSeen=${ctx.fmt(d.bpc?.agentLastSeenOn)}, agent=${d.bpc?.agentVersion || '—'}`}
        titleWhenAbsent={ctx.bpcStale
          ? `Stale: last seen ${ctx.fmt(d.bpc?.agentLastSeenOn)}`
          : 'Missing from BPC'}
        naTitle={d.naDrmmBpc ? `BPC agents do not run on ${d.os} devices` : undefined}
      />
    ),
    csv: (d, ctx) => (ctx.bpcLoaded ? presenceCsv(d.hasBpc) : ''),
  },
  {
    key: 'workloadCount', label: 'In #', w: 70, align: 'center', filterType: 'select',
    cellClass: 'px-3 py-2 text-center',
    render: (d) => <CountPill value={d.workloadCount} max={d.applicableMax} />,
    csv: (d) => d.workloadCount,
  },
  {
    key: 'os', label: 'OS', w: 110, align: 'left', filterType: 'multi-select',
    cellClass: 'px-3 py-2',
    render: (d) => <OSBadge value={d.os} />,
    csv: (d) => d.os || '',
  },
  {
    key: 'lastSeen', label: 'Last Seen by M365', w: 200, align: 'left', filterType: 'date-op',
    cellClass: 'px-3 py-2 text-gray-400',
    render: (d, ctx) => ctx.fmt(d.lastSeen),
    csv: (d) => d.lastSeen || '',
  },
  {
    key: 'lastSeenDrmm', label: 'Last Seen by DRMM', w: 180, align: 'left', filterType: 'date-op',
    cellClass: 'px-3 py-2 text-gray-400',
    render: (d, ctx) => ctx.fmt(d.lastSeenDrmm),
    csv: (d) => d.lastSeenDrmm || '',
  },
  {
    key: 'lastSeenEntra', label: 'Last Seen by Entra', w: 180, align: 'left', filterType: 'date-op',
    cellClass: 'px-3 py-2 text-gray-400',
    render: (d, ctx) => ctx.fmt(d.lastSeenEntra),
    csv: (d) => d.lastSeenEntra || '',
  },
  {
    key: 'lastSeenIntune', label: 'Last Seen by Intune', w: 180, align: 'left', filterType: 'date-op',
    cellClass: 'px-3 py-2 text-gray-400',
    render: (d, ctx) => ctx.fmt(d.lastSeenIntune),
    csv: (d) => d.lastSeenIntune || '',
  },
  {
    key: 'lastSeenBpc', label: 'Last Seen by BPC', w: 180, align: 'left', filterType: 'date-op',
    cellClass: 'px-3 py-2 text-gray-400',
    render: (d, ctx) => ctx.fmt(d.lastSeenBpc),
    csv: (d) => d.lastSeenBpc || '',
  },
  {
    key: 'lastUser', label: 'Last User by M365', w: 220, align: 'left', filterType: 'text',
    cellClass: 'px-3 py-2 text-gray-400 truncate',
    cellTitle: (d) => d.lastUser,
    render: (d) => d.lastUser || '—',
    csv: (d) => d.lastUser || '',
  },
];

// Last-seen column keys (used for TZ-suffix injection in the header label).
const LAST_SEEN_COL_KEYS = new Set(['lastSeen', 'lastSeenDrmm', 'lastSeenEntra', 'lastSeenIntune', 'lastSeenBpc']);

// Column-visibility persistence. All five last-seen columns hide by default — including
// the previously-visible M365 one — so the default table is compact. Users can toggle
// any column except Device Name via the ColumnPickerButton in the toolbar.
const HIDDEN_COLS_STORAGE_KEY = 'coverage-comparison-hidden-cols-v1';
const DEFAULT_HIDDEN_COLS = ['lastSeen', 'lastSeenDrmm', 'lastSeenEntra', 'lastSeenIntune', 'lastSeenBpc'];

// Workloads with a last-seen-type timestamp older than STALE_DAYS before the chosen snapshot
// are treated as effectively absent (red dot, drop from In-# count, drop from Source/Target gap,
// 'No' in CSV). Tooltip on the dot still differentiates "stale (last seen YYYY-MM-DD)" from
// "missing".
const STALE_DAYS = 30;

// Canonicalize OS values across the five sources. Same physical OS can come back with different
// casing / spacing depending on the data source (Entra returns "Windows", DRMM regex outputs
// "Windows 11", BPC returns "OSX", etc.) which makes the multi-select OS filter list duplicates.
// Normalize to a single display form so the filter buckets dedupe correctly.
function canonOs(s) {
  if (!s) return '';
  const lc = String(s).trim().toLowerCase();
  if (!lc) return '';
  // Single-token canonicalization
  if (lc === 'windows' || lc === 'win' || lc === 'win32') return 'Windows';
  if (lc === 'macos' || lc === 'mac os' || lc === 'mac os x' || lc === 'osx') return 'macOS';
  if (lc === 'ios')                       return 'iOS';
  if (lc === 'ipad' || lc === 'ipados')   return 'iPadOS';
  if (lc === 'iphone')                    return 'iOS';
  if (lc === 'android')                   return 'Android';
  if (lc === 'androidforwork')            return 'AndroidForWork';
  if (lc === 'androidenterprise')         return 'AndroidEnterprise';
  if (lc === 'linux')                     return 'Linux';
  if (lc === 'chromeos' || lc === 'chrome os') return 'ChromeOS';
  if (lc === 'vmware')                    return 'VMware';
  // Multi-token (e.g. "Windows 11", "Windows Server", "macOS Sonoma", "VMware ESXi") —
  // preserve granularity but normalize casing per token. Brand tokens (Windows, macOS, iOS,
  // iPadOS, VMware) keep their canonical form; other tokens are title-cased.
  const tokens = String(s).trim().split(/\s+/).map((t) => {
    const tlc = t.toLowerCase();
    if (tlc === 'windows') return 'Windows';
    if (tlc === 'macos')   return 'macOS';
    if (tlc === 'ios')     return 'iOS';
    if (tlc === 'ipados')  return 'iPadOS';
    if (tlc === 'osx')     return 'macOS';
    if (tlc === 'vmware')  return 'VMware';
    return t.charAt(0).toUpperCase() + t.slice(1).toLowerCase();
  });
  return tokens.join(' ');
}

// Workloads that don't apply to mobile (iOS / iPadOS / Android) or hypervisor (VMware*) OSes.
// DRMM and BPC are endpoint-management agents — neither installs on a phone or a hypervisor.
// For these OS+workload combinations, the presence cell shows "—" with an N/A tooltip rather
// than a red "missing" dot, the workload is excluded from the In-# count, and Source/Target
// gap analysis ignores the row for that workload (so iOS devices don't appear as DRMM gaps).
// Entra trustType buckets. The directory uses 'AzureAd' and 'ServerAd' (= hybrid) for
// corporate-joined devices, and 'Workplace' for BYOD-registered devices. Joined is strictly
// stronger than Registered — it's what underpins skip_if_entra_joined on detection rules.
function isEntraJoined(trustType) {
  return trustType === 'AzureAd' || trustType === 'ServerAd';
}
function isEntraRegistered(trustType) {
  return trustType === 'Workplace';
}

function isDrmmBpcNotApplicable(canonOsValue) {
  if (!canonOsValue) return false;
  if (canonOsValue === 'iOS' || canonOsValue === 'iPadOS') return true;
  // Match Android and any Android-family variant (Android, AndroidForWork, AndroidEnterprise).
  if (/^android/i.test(canonOsValue)) return true;
  // Match VMware and any VMware variant (e.g. "VMware ESXi", "VMware vSphere").
  if (/^vmware(\s|$)/i.test(canonOsValue)) return true;
  return false;
}

const initFilters = () => ({
  displayName: '',
  hasEntraJoined: '', hasEntraRegistered: '', hasIntune: '', hasM365: '', hasDrmm: '', hasBpc: '',
  workloadCount: '', os: '',
  lastSeen: '',       lastSeen_op: '>=',
  lastSeenDrmm: '',   lastSeenDrmm_op: '>=',
  lastSeenEntra: '',  lastSeenEntra_op: '>=',
  lastSeenIntune: '', lastSeenIntune_op: '>=',
  lastSeenBpc: '',    lastSeenBpc_op: '>=',
  lastUser: '',
});

// ── Cell renderers ────────────────────────────────────────────────────────────

function PresenceCell({ value, loaded, titleWhenPresent, titleWhenAbsent, naTitle }) {
  // N/A takes priority — value=null means "this workload doesn't apply to this device's OS"
  // (e.g. DRMM/BPC on iOS/Android/VMware). Showing "Workload not loaded" or a red "missing"
  // dot would be misleading.
  if (value === null) return <span className="text-gray-600 text-xs" title={naTitle || 'Not applicable'}>—</span>;
  if (!loaded) return <span className="text-gray-600 text-xs" title="Workload not loaded">—</span>;
  return value
    ? <span className="inline-flex items-center justify-center" title={titleWhenPresent}>
        <span className="h-2 w-2 rounded-full bg-green-400" />
      </span>
    : <span className="inline-flex items-center justify-center" title={titleWhenAbsent}>
        <span className="h-2 w-2 rounded-full bg-red-500" />
      </span>;
}

// Faint green dot used in the "Entra Registered" column when the device is actually
// Entra Joined. Joined is strictly stronger than Registered, so a red "missing" dot
// would alarm an analyst about a non-issue. The dot is presentational only — column
// filters, gap analysis, CSV export, and the In-# count all keep the strict booleans.
function FaintPresenceDot({ title }) {
  return (
    <span className="inline-flex items-center justify-center" title={title}>
      <span className="h-2 w-2 rounded-full bg-green-400/30" />
    </span>
  );
}

function CountPill({ value, max }) {
  const tone = value === max ? 'bg-gray-800/60 text-gray-300 border-gray-700/50'
             : value === 1   ? 'bg-red-900/40 text-red-300 border-red-700/50'
             :                 'bg-amber-900/40 text-amber-300 border-amber-700/50';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 text-xs rounded border ${tone}`}>
      {value}/{max}
    </span>
  );
}

// ── CSV export helpers ────────────────────────────────────────────────────────

function csvEscape(v) {
  if (v == null) return '';
  const s = String(v);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

// Tri-state Yes/No/N/A for the per-workload columns: null \u2192 "N/A" (workload doesn't apply
// to this OS), false \u2192 "No", true \u2192 "Yes". For BPC, also render empty when the tenant has
// no mapping (column was never queried).
function presenceCsv(v) {
  if (v === null) return 'N/A';
  return v ? 'Yes' : 'No';
}
// Column-driven CSV export. Header order + row encoding both follow the passed-in
// `visibleCols` array, so hiding a column via the picker drops it from the CSV too.
// `ctx` carries the per-call state the csv functions need ({ bpcLoaded }).
function rowsToCsv(rows, visibleCols, ctx) {
  const lines = [visibleCols.map((c) => csvEscape(c.label)).join(',')];
  for (const r of rows) {
    lines.push(
      visibleCols.map((c) => csvEscape(c.csv ? c.csv(r, ctx) : (r[c.key] ?? ''))).join(',')
    );
  }
  return '\uFEFF' + lines.join('\r\n');  // BOM for Excel
}

function downloadBlob(content, filename) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ── Resize hook (key-keyed widths) ────────────────────────────────────────────
//
// The shared `useResizableCols(defaults)` keys widths by position, so toggling a
// column's visibility shifts every other column's width up or down by one slot.
// This local variant keys widths by `col.key`, then projects back to a
// position-indexed `colWidths` array that mirrors the current `cols` argument.
// New keys (e.g. just-introduced last-seen columns) pick up their default width
// the first time they appear.
function useKeyedColWidths(cols) {
  const [byKey, setByKey] = useState(() =>
    Object.fromEntries(cols.map((c) => [c.key, c.w]))
  );
  useEffect(() => {
    setByKey((prev) => {
      let next = prev;
      for (const c of cols) {
        if (next[c.key] === undefined) {
          if (next === prev) next = { ...prev };
          next[c.key] = c.w;
        }
      }
      return next === prev ? prev : next;
    });
  }, [cols]);
  const thRefs = useRef([]);
  const tableRef = useRef(null);
  const startResize = useCallback((e, idx) => {
    const col = cols[idx];
    if (!col) return;
    e.preventDefault();
    const startX = e.clientX;
    const startW = thRefs.current[idx]?.offsetWidth ?? byKey[col.key] ?? col.w;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMove = (mv) => {
      setByKey((p) => ({ ...p, [col.key]: Math.max(50, startW + mv.clientX - startX) }));
    };
    const onUp = () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [cols, byKey]);
  const colWidths = cols.map((c) => byKey[c.key] ?? c.w);
  return { colWidths, thRefs, tableRef, startResize };
}

// ── Column picker (toolbar popover) ───────────────────────────────────────────
//
// Toolbar button + popover for show/hide on every column except Device Name.
// Persists to localStorage via the `hiddenCols` setter the parent passes in.
function ColumnPickerButton({ allCols, hiddenCols, setHiddenCols, defaultHidden }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const togglable = allCols.filter((c) => c.key !== 'displayName');
  const hiddenCount = togglable.filter((c) => hiddenCols.has(c.key)).length;

  const toggle = (key) => {
    setHiddenCols((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const reset = () => setHiddenCols(new Set(defaultHidden));

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className="btn-secondary text-xs px-2.5 py-1 flex items-center gap-1.5"
        title="Show or hide columns"
      >
        <ViewColumnsIcon className="h-3.5 w-3.5" />
        Columns{hiddenCount > 0 ? ` (${hiddenCount} hidden)` : ''}
      </button>
      {open && (
        <div className="absolute z-30 mt-1 right-0 min-w-[220px] max-h-80 overflow-y-auto bg-gray-900 border border-gray-700 rounded shadow-lg py-1">
          {togglable.map((c) => {
            const isHidden = hiddenCols.has(c.key);
            return (
              <label
                key={c.key}
                className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={!isHidden}
                  onChange={() => toggle(c.key)}
                  className="h-3 w-3 accent-blue-500"
                />
                <span className="truncate">{c.label}</span>
              </label>
            );
          })}
          <div className="border-t border-gray-800 mt-1 pt-1">
            <button
              type="button"
              onClick={reset}
              className="w-full text-left px-3 py-1.5 text-[11px] text-blue-400 hover:bg-gray-800"
            >
              Reset to defaults
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CoverageComparisonPage() {
  const [tenants, setTenants]               = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [snapshotDate, setSnapshotDate]     = useState(() => new Date().toISOString().slice(0, 10));

  // Per-workload data
  const [m365Devices, setM365Devices]     = useState(null);
  const [drmmDevices, setDrmmDevices]     = useState(null);
  const [bpcDevices,  setBpcDevices]      = useState(null);
  const [entraDevices, setEntraDevices]   = useState(null);
  const [intuneDevices, setIntuneDevices] = useState(null);
  const [m365Loading, setM365Loading]     = useState(false);
  const [drmmLoading, setDrmmLoading]     = useState(false);
  const [bpcLoading,  setBpcLoading]      = useState(false);
  const [entraLoading, setEntraLoading]   = useState(false);
  const [intuneLoading, setIntuneLoading] = useState(false);
  const [m365Error,   setM365Error]       = useState(null);
  const [drmmError,   setDrmmError]       = useState(null);
  const [bpcError,    setBpcError]        = useState(null);
  const [entraError,  setEntraError]      = useState(null);
  const [intuneError, setIntuneError]     = useState(null);
  const [m365LoadTime, setM365LoadTime]   = useState(null);
  const [drmmLoadTime, setDrmmLoadTime]   = useState(null);
  const [bpcLoadTime,  setBpcLoadTime]    = useState(null);
  const [entraLoadTime, setEntraLoadTime] = useState(null);
  const [intuneLoadTime, setIntuneLoadTime] = useState(null);

  // BPC tenants list — fetched lazily inside handleLoad (after the user picks a tenant
  // and clicks Load). Doing this on mount risks a 401-driven forceRelogin that bounces
  // the user back to home before tenants have a chance to render.
  const [bpcTenantsList, setBpcTenantsList] = useState(null);

  // Look up the BPC tenant ID mapped to the currently-selected M365 tenant.
  // null until bpcTenantsList has been fetched (we treat unknown as unmapped).
  const bpcMapping = useMemo(() => {
    if (!selectedTenant || !bpcTenantsList) return null;
    const match = bpcTenantsList.find((b) => b.m365TenantId === selectedTenant);
    return match?.bpcTenantId || null;
  }, [bpcTenantsList, selectedTenant]);

  // Filters / sort / pagination
  const [filters, setFilters]         = useState(initFilters());
  const [sortField, setSortField]     = useState('');
  const [sortDir, setSortDir]         = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize]       = useState(10);

  // Source/Target gap filter
  const [sourceWl, setSourceWl] = useState('');
  const [targetWl, setTargetWl] = useState('');

  // 30-day staleness filter — when on (default), workloads with lastSeen > 30 days before
  // snapshot are treated as effectively absent. When off, presence reflects raw inventory hits
  // regardless of lastSeen age.
  const [staleFilterEnabled, setStaleFilterEnabled] = useState(true);

  // Display timezone — defaults to browser-detected local zone, persists to the same global
  // TZ_STORAGE_KEY shared with the alerts/timeline pages. The picker uses TIMEZONE_OPTIONS,
  // so 'Local' / UTC / 6 US zones are all available.
  const [selectedTz, setSelectedTz] = useState(getCoverageDefaultTz);
  useEffect(() => {
    try { localStorage.setItem(TZ_STORAGE_KEY, selectedTz); } catch { /* ignore */ }
  }, [selectedTz]);

  // Column visibility — Set of column keys the user has hidden. Persists per-browser.
  // Default = all five last-seen columns hidden (incl. the M365 one that used to be
  // visible by default). Toggleable via ColumnPickerButton in the toolbar.
  const [hiddenCols, setHiddenCols] = useState(() => {
    try {
      const raw = localStorage.getItem(HIDDEN_COLS_STORAGE_KEY);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set(DEFAULT_HIDDEN_COLS);
  });
  useEffect(() => {
    try {
      localStorage.setItem(HIDDEN_COLS_STORAGE_KEY, JSON.stringify([...hiddenCols]));
    } catch { /* ignore */ }
  }, [hiddenCols]);

  // TZ-suffix the five last-seen labels (e.g. "Last Seen by Entra (ET)") and drop the
  // hidden ones. Device Name is forced visible regardless of `hiddenCols`. Re-derived
  // when the user picks a new TZ or toggles visibility.
  const visibleCols = useMemo(() => {
    const tzAdjusted = COMPARE_COLS.map((c) =>
      LAST_SEEN_COL_KEYS.has(c.key)
        ? { ...c, label: `${c.label} (${getTzLabel(selectedTz)})` }
        : c
    );
    return tzAdjusted.filter((c) => c.key === 'displayName' || !hiddenCols.has(c.key));
  }, [hiddenCols, selectedTz]);

  // Load tenants on mount (BPC mapping is fetched on demand inside handleLoad).
  useEffect(() => {
    fetchTenants()
      .then((data) => {
        const active = (Array.isArray(data) ? data : []).filter((t) => t.Status !== 'inactive');
        setTenants(active.sort((a, b) => (a.FriendlyName || '').localeCompare(b.FriendlyName || '')));
      })
      .catch(() => {})
      .finally(() => setTenantsLoading(false));
  }, []);

  // Selected tenant friendly + short name (for export filenames)
  const selectedTenantInfo = useMemo(() => {
    return tenants.find((t) => t.TenantID === selectedTenant);
  }, [tenants, selectedTenant]);

  const tenantShortName = (selectedTenantInfo?.ShortName
                          || selectedTenantInfo?.FriendlyName
                          || selectedTenant
                          || 'tenant').replace(/[^A-Za-z0-9_-]+/g, '-').slice(0, 40);

  // ── Load workloads ──────────────────────────────────────────────────────────

  const handleLoad = useCallback(async () => {
    if (!selectedTenant) return;

    setM365Devices(null);
    setDrmmDevices(null);
    setBpcDevices(null);
    setEntraDevices(null);
    setIntuneDevices(null);
    setM365Loading(true);
    setDrmmLoading(true);
    setBpcLoading(false);
    setEntraLoading(true);
    setIntuneLoading(true);
    setM365Error(null);
    setDrmmError(null);
    setBpcError(null);
    setEntraError(null);
    setIntuneError(null);
    setM365LoadTime(null);
    setDrmmLoadTime(null);
    setBpcLoadTime(null);
    setEntraLoadTime(null);
    setIntuneLoadTime(null);
    setFilters(initFilters());
    setSortField('');
    setSortDir('asc');
    setCurrentPage(1);
    setSourceWl('');
    setTargetWl('');

    // Fetch BPC mapping list lazily (first Load click). If the call fails, treat as unmapped
    // and continue with M365 + DRMM only — we never let a BPC failure block the page.
    let bpcList = bpcTenantsList;
    if (bpcList === null) {
      try {
        const result = await fetchBpcTenants();
        bpcList = Array.isArray(result) ? result : [];
      } catch (err) {
        console.warn('[Comparison] fetchBpcTenants failed; treating tenant as unmapped:', err);
        bpcList = [];
      }
      setBpcTenantsList(bpcList);
    }
    const mappedBpcId = (bpcList.find((b) => b.m365TenantId === selectedTenant) || {}).bpcTenantId || null;

    const t0 = performance.now();
    const today = new Date().toISOString().slice(0, 10);
    const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10);
    const isToday = snapshotDate === today;
    const baseOpts = snapshotDate ? { snapshotDate } : {};

    const loadOne = (workload, setter, setTime, setErr, setLoad, extraOpts = {}, tenantArg = selectedTenant) => {
      const opts = { ...baseOpts, ...extraOpts, workload };
      fetchDeviceInventory(tenantArg, opts)
        .then((data) => {
          const arr = Array.isArray(data) ? data : [];
          if (arr.length === 0 && isToday) {
            return fetchDeviceInventory(tenantArg, { ...opts, snapshotDate: yesterday })
              .then((d2) => {
                const arr2 = Array.isArray(d2) ? d2 : [];
                if (arr2.length > 0) setSnapshotDate(yesterday);
                return arr2;
              });
          }
          return arr;
        })
        .then((arr) => {
          const secs = ((performance.now() - t0) / 1000).toFixed(1);
          setTime(secs);
          setter(arr);
        })
        .catch((err) => setErr(err.message || `Failed to load ${workload.toUpperCase()} devices.`))
        .finally(() => setLoad(false));
    };

    loadOne('m365',   setM365Devices,   setM365LoadTime,   setM365Error,   setM365Loading);
    loadOne('drmm',   setDrmmDevices,   setDrmmLoadTime,   setDrmmError,   setDrmmLoading);
    loadOne('entra',  setEntraDevices,  setEntraLoadTime,  setEntraError,  setEntraLoading);
    loadOne('intune', setIntuneDevices, setIntuneLoadTime, setIntuneError, setIntuneLoading);
    if (mappedBpcId) {
      setBpcLoading(true);
      loadOne('bpc', setBpcDevices, setBpcLoadTime, setBpcError, setBpcLoading,
              { bpcTenantId: mappedBpcId }, '');
    }
  }, [selectedTenant, snapshotDate, bpcTenantsList]);

  // ── Build comparison rows ───────────────────────────────────────────────────

  const allLoaded = !m365Loading && !drmmLoading && !bpcLoading && !entraLoading && !intuneLoading
                 && (m365Devices !== null || drmmDevices !== null || bpcDevices !== null
                     || entraDevices !== null || intuneDevices !== null);

  const bpcLoaded = bpcMapping != null;  // Will become true when Part B plumbs in mapping

  const rows = useMemo(() => {
    if (!allLoaded) return [];

    // Multi-key union-find across the five workloads. Each workload row carries 0..N identity
    // keys: AAD device GUID (M365/Entra/Intune), serial number (Intune/DRMM), MAC address
    // (DRMM/BPC), normalized hostname (last-resort fallback for everyone). A row joins an
    // existing cluster if ANY key matches; otherwise it starts a new cluster. When a row
    // pulls in keys that touch multiple existing clusters, those clusters are merged.
    //
    // Why each key is here:
    //   aad:  rock-solid GUID, unifies M365 ↔ Entra ↔ Intune (Apple device naming varies wildly
    //         across these three — Entra/M365 see Apple's user-facing name, Intune sees the
    //         local hostname).
    //   sn:   bridges Intune ↔ DRMM (Apple hardware serial / Windows BIOS serial — same value).
    //   mac:  bridges DRMM ↔ BPC for devices without serial in BPC. Locally-administered
    //         (randomized) MACs and zero-MACs are excluded — they false-match across devices.
    //   hn:   fallback when no stronger key is present (e.g. M365-only rows pre-AAD-registration).
    //         Suffixes (.local/.localdomain/.lan/.home) are stripped before normalization to
    //         catch the common `JimsMac.local` ↔ `JimsMac` mismatch.
    const PLACEHOLDER_GUID = '00000000-0000-0000-0000-000000000000';
    const ZERO_MAC = '000000000000';

    function normHost(s) {
      if (!s) return '';
      let t = String(s).trim().toLowerCase();
      // Strip common DNS suffixes (single label only — anything beyond first dot is suffix-like
      // for endpoint hostnames).
      t = t.replace(/\.(local|localdomain|lan|home|corp)$/i, '');
      // Collapse non-alphanumeric runs to nothing so "Josh.Muston MacBook Pro" matches
      // "JoshMuston-MacBook-Pro". This is NOT fuzzy matching (no Levenshtein) — purely
      // separator-normalization. "MAC" stays "mac" and won't collide with "macbook".
      t = t.replace(/[^a-z0-9]+/g, '');
      return t;
    }

    function parseMacs(jsonStr) {
      if (!jsonStr) return [];
      let arr;
      try { arr = JSON.parse(jsonStr); } catch { return []; }
      if (!Array.isArray(arr)) return [];
      const out = [];
      for (const m of arr) {
        if (typeof m !== 'string') continue;
        const bare = m.replace(/[^0-9a-fA-F]/g, '').toLowerCase();
        if (bare.length !== 12) continue;            // need 48 bits
        if (bare === ZERO_MAC) continue;             // BPC stuffs zero-MACs for placeholder NICs
        // Locally-administered bit (0x02 in first octet) → randomized; not a stable hardware ID.
        const firstOctet = parseInt(bare.slice(0, 2), 16);
        if ((firstOctet & 0x02) === 0x02) continue;
        out.push(bare);
      }
      return out;
    }

    function deviceKeys(workload, d) {
      const keys = [];
      if (workload === 'm365' || workload === 'entra') {
        const aad = (d.deviceId || '').toLowerCase();
        if (aad && aad !== PLACEHOLDER_GUID) keys.push(`aad:${aad}`);
      } else if (workload === 'intune') {
        const aad = (d.azureADDeviceId || '').toLowerCase();
        if (aad && aad !== PLACEHOLDER_GUID) keys.push(`aad:${aad}`);
      }
      if (workload === 'intune' || workload === 'drmm') {
        const sn = (d.serialNumber || '').trim().toLowerCase();
        if (sn) keys.push(`sn:${sn}`);
      }
      if (workload === 'drmm' || workload === 'bpc') {
        for (const mac of parseMacs(d.macAddresses)) keys.push(`mac:${mac}`);
      }
      let name = '';
      if (workload === 'm365')   name = d.deviceName;
      if (workload === 'entra')  name = d.displayName;
      if (workload === 'intune') name = d.deviceName || d.managedDeviceName;
      if (workload === 'drmm')   name = d.hostname;
      if (workload === 'bpc')    name = d.hostname || d.displayName;
      const hn = normHost(name);
      if (hn) keys.push(`hn:${hn}`);
      return { keys, name };
    }

    // Display-name preference once a cluster is built: M365 → Entra → Intune → DRMM → BPC.
    function pickDisplayName(cluster) {
      return cluster.m365?.deviceName
          || cluster.entra?.displayName
          || cluster.intune?.deviceName
          || cluster.intune?.managedDeviceName
          || cluster.drmm?.hostname
          || cluster.bpc?.hostname
          || cluster.bpc?.displayName
          || cluster.fallbackName
          || '';
    }

    // keyIndex maps `aad:xyz`/`sn:xyz`/`mac:xyz`/`hn:xyz` → cluster object reference (same
    // reference across multiple keys for one cluster — merging just remaps keys).
    const keyIndex = new Map();
    const clusters = new Set();

    // Per-workload last-seen field — used to pick the freshest row when a cluster has more
    // than one row from the same workload. DRMM in particular ghosts: when a device is
    // renamed, DRMM keeps the old record (`deleted=false`, agent-reported lastSeen frozen)
    // alongside the new one. Both rows share the same BIOS serial so the multi-key join
    // correctly identifies them as the same device; without a freshness preference the row
    // returned first by Az Table (RowKey-ordered) wins and the staleness filter false-flags
    // the device as stale.
    const rowLastSeen = (wl, row) => {
      if (!row) return '';
      if (wl === 'm365')   return row.lastSeenUtc || '';
      if (wl === 'drmm')   return row.lastSeen || '';
      if (wl === 'entra')  return row.approximateLastSignInDateTime || '';
      if (wl === 'intune') return row.lastSyncDateTime || '';
      if (wl === 'bpc')    return row.agentLastSeenOn || '';
      return '';
    };
    const preferFresher = (wl, existing, candidate) => {
      if (!existing)  return candidate;
      if (!candidate) return existing;
      return rowLastSeen(wl, candidate) > rowLastSeen(wl, existing) ? candidate : existing;
    };

    const newCluster = (firstName) => {
      const c = {
        hasEntra: false, hasIntune: false, hasM365: false, hasDrmm: false, hasBpc: false,
        entra: null, intune: null, m365: null, drmm: null, bpc: null,
        fallbackName: firstName || '',
      };
      clusters.add(c);
      return c;
    };

    const mergeInto = (target, source) => {
      if (target === source) return target;
      for (const wl of ['m365', 'entra', 'intune', 'drmm', 'bpc']) {
        const flag = 'has' + wl[0].toUpperCase() + wl.slice(1);
        if (source[flag]) target[flag] = true;
        target[wl] = preferFresher(wl, target[wl], source[wl]);
      }
      if (!target.fallbackName) target.fallbackName = source.fallbackName;
      // Reindex all keys that pointed at `source` to point at `target`.
      for (const [k, v] of keyIndex.entries()) {
        if (v === source) keyIndex.set(k, target);
      }
      clusters.delete(source);
      return target;
    };

    const ingest = (workload, d) => {
      const { keys, name } = deviceKeys(workload, d);
      if (keys.length === 0) return;
      // Find every existing cluster touched by any of this row's keys.
      const touched = new Set();
      for (const k of keys) {
        const c = keyIndex.get(k);
        if (c) touched.add(c);
      }
      let cluster;
      if (touched.size === 0) {
        cluster = newCluster(name);
      } else {
        // Pick one as the survivor; merge any others into it.
        const iter = touched.values();
        cluster = iter.next().value;
        for (let nxt = iter.next(); !nxt.done; nxt = iter.next()) {
          mergeInto(cluster, nxt.value);
        }
      }
      // Index every key for this cluster.
      for (const k of keys) keyIndex.set(k, cluster);
      // Record presence + raw row.
      const flag = 'has' + workload[0].toUpperCase() + workload.slice(1);
      cluster[flag] = true;
      cluster[workload] = preferFresher(workload, cluster[workload], d);
    };

    // Ingest order: Intune first (carries BOTH AAD GUID and serial — anchors the most join
    // edges). Then Entra/M365 (AAD GUID) so they hook into Intune anchors. Then DRMM (serial
    // → Intune; MAC → BPC; hostname fallback). Then BPC (MAC → DRMM; hostname fallback).
    // Inactive devices are dropped so they don't surface as gaps or inflate workload counts.
    (intuneDevices ?? []).forEach((d) => ingest('intune', d));
    (entraDevices  ?? []).forEach((d) => {
      if (d.accountEnabled === false) return;
      ingest('entra', d);
    });
    (m365Devices   ?? []).forEach((d) => ingest('m365', d));
    (drmmDevices   ?? []).forEach((d) => {
      if (d.deleted === true) return;
      ingest('drmm', d);
    });
    (bpcDevices    ?? []).forEach((d) => ingest('bpc', d));

    // Build the same { name, displayName, hasX, X } shape downstream code expects. `name`
    // was previously the normalized hostname (used as a React key + stable identity); use the
    // first canonical key we can find on the cluster, falling back to displayName for stability.
    const map = new Map();
    for (const c of clusters) {
      const dn = pickDisplayName(c);
      const idKey = c.intune?.azureADDeviceId
                 || c.entra?.deviceId
                 || c.m365?.deviceId
                 || c.intune?.serialNumber
                 || c.drmm?.serialNumber
                 || normHost(dn)
                 || `__c${map.size}`;
      map.set(idKey, {
        name: idKey,
        displayName: dn,
        hasEntra: c.hasEntra, hasIntune: c.hasIntune, hasM365: c.hasM365, hasDrmm: c.hasDrmm, hasBpc: c.hasBpc,
        entra: c.entra, intune: c.intune, m365: c.m365, drmm: c.drmm, bpc: c.bpc,
      });
    }

    // Compute the fresh-vs-stale cutoff once per render when the staleness filter is on.
    // Anchored to snapshotDate (the user-selected snapshot), not "now" — staleness measures
    // activity relative to the snapshot the analyst is investigating. Lexicographic
    // YYYY-MM-DD comparison works because all upstream last-seen fields are ISO 8601.
    const cutoff = (() => {
      const base = snapshotDate ? new Date(`${snapshotDate}T00:00:00Z`) : new Date();
      base.setUTCDate(base.getUTCDate() - STALE_DAYS);
      return base.toISOString().slice(0, 10);
    })();
    // When the staleness filter is off, every timestamp is treated as fresh — collapsing the
    // fresh-vs-inventory distinction so `freshX === inventoryX` and the page reverts to its
    // pre-v1.47.6 behaviour (raw inventory presence regardless of lastSeen age).
    const isFresh = staleFilterEnabled
      ? (ts) => !!ts && String(ts).slice(0, 10) >= cutoff
      : () => true;

    return Array.from(map.values()).map((r) => {
      // Save raw inventory hits before overwriting `hasX` with fresh-aware values — needed so
      // the cell tooltip can distinguish "stale" (in inventory but old) from "missing" (not in
      // inventory at all).
      const inventoryM365   = r.hasM365;
      const inventoryDrmm   = r.hasDrmm;
      const inventoryEntra  = r.hasEntra;
      const inventoryIntune = r.hasIntune;
      const inventoryBpc    = r.hasBpc;

      const freshM365   = inventoryM365   && isFresh(r.m365?.lastSeenUtc);
      const freshDrmm   = inventoryDrmm   && isFresh(r.drmm?.lastSeen);
      const freshEntra  = inventoryEntra  && isFresh(r.entra?.approximateLastSignInDateTime);
      const freshIntune = inventoryIntune && isFresh(r.intune?.lastSyncDateTime);
      const freshBpc    = inventoryBpc    && isFresh(r.bpc?.agentLastSeenOn);

      // Split Entra by trustType. Joined and Registered are mutually exclusive sub-states
      // of the same workload — they don't double-count toward `workloadCount`/`applicableMax`.
      const trustType = r.entra?.trustType || '';
      const freshEntraJoined         = freshEntra     && isEntraJoined(trustType);
      const freshEntraRegistered     = freshEntra     && isEntraRegistered(trustType);
      const inventoryEntraJoined     = inventoryEntra && isEntraJoined(trustType);
      const inventoryEntraRegistered = inventoryEntra && isEntraRegistered(trustType);

      const bpcOs = r.bpc?.osName === 'OSX' ? 'MacOS' : r.bpc?.osName;
      const os = canonOs(r.m365?.osType || r.entra?.operatingSystem || r.drmm?.osCommonName || bpcOs || '');
      // DRMM and BPC don't run on mobile / hypervisor OSes — surface as N/A (null) so the dot
      // shows "—" instead of red, the count drops them, and gap analysis skips them.
      const naDrmmBpc   = isDrmmBpcNotApplicable(os);
      const hasDrmm     = naDrmmBpc ? null : freshDrmm;
      const hasBpc      = naDrmmBpc ? null : freshBpc;

      const workloadCount = (freshEntra ? 1 : 0) + (freshIntune ? 1 : 0) + (freshM365 ? 1 : 0)
                          + (hasDrmm === true ? 1 : 0) + (hasBpc === true ? 1 : 0);
      // Max coverage reachable for this row's OS + tenant config. Entra+Intune+M365 are always
      // applicable. DRMM is dropped from max for mobile/VMware. BPC is dropped both for mobile/
      // VMware and when the tenant has no BPC mapping.
      const applicableMax = 3
                          + (naDrmmBpc ? 0 : 1)                          // DRMM
                          + (naDrmmBpc ? 0 : (bpcMapping != null ? 1 : 0)); // BPC
      // "Last Seen by M365" and "Last User by M365" are intentionally single-source signals
      // (M365 sign-in-derived inventory).
      return {
        ...r,
        hasM365:   freshM365,
        hasDrmm,
        hasEntra:  freshEntra,
        hasEntraJoined:     freshEntraJoined,
        hasEntraRegistered: freshEntraRegistered,
        hasIntune: freshIntune,
        hasBpc,
        inventoryM365, inventoryDrmm, inventoryEntra, inventoryEntraJoined, inventoryEntraRegistered, inventoryIntune, inventoryBpc,
        naDrmmBpc,
        workloadCount,
        applicableMax,
        os,
        lastSeen:       r.m365?.lastSeenUtc || '',
        lastSeenDrmm:   r.drmm?.lastSeen || '',
        lastSeenEntra:  r.entra?.approximateLastSignInDateTime || '',
        lastSeenIntune: r.intune?.lastSyncDateTime || '',
        lastSeenBpc:    r.bpc?.agentLastSeenOn || '',
        lastUser: r.m365?.lastUserUpn || '',
      };
    });
  }, [allLoaded, m365Devices, drmmDevices, bpcDevices, entraDevices, intuneDevices, snapshotDate, staleFilterEnabled, bpcMapping]);

  // Distinct values for select filters
  const distinct = useMemo(() => ({
    workloadCount: [...new Set(rows.map((r) => String(r.workloadCount)))].sort(),
    os: [...new Set(rows.map((r) => r.os).filter(Boolean))].sort(),
  }), [rows]);

  const setFilter = useCallback((key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initFilters());
    setSourceWl('');
    setTargetWl('');
    setCurrentPage(1);
  }, []);

  // Apply staleness filter → column filters → gap filter → sort → paginate
  // When the "Hide stale" toggle is on, drop rows where every workload is either
  // stale or absent (workloadCount === 0). Without this, the toolbar device count
  // stays constant regardless of toggle state — only the per-row dots change colour.
  const filtered = useMemo(() => {
    let r = staleFilterEnabled ? rows.filter((row) => row.workloadCount > 0) : rows;
    r = applyColFilters(r, visibleCols, filters);
    if (sourceWl && targetWl && sourceWl !== targetWl) {
      const srcKey = `has${sourceWl[0].toUpperCase()}${sourceWl.slice(1)}`;
      const tgtKey = `has${targetWl[0].toUpperCase()}${targetWl.slice(1)}`;
      r = r.filter((row) => row[srcKey] === true && row[tgtKey] === false);
    }
    if (sortField) r = [...r].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    return r;
  }, [rows, filters, sortField, sortDir, sourceWl, targetWl, staleFilterEnabled, visibleCols]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) { setSortDir((d) => d === 'asc' ? 'desc' : 'asc'); return field; }
      setSortDir('asc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  // Source/Target — selecting same as the other clears the other
  const onSourceChange = (v) => {
    setSourceWl(v);
    if (v && v === targetWl) setTargetWl('');
    setCurrentPage(1);
  };
  const onTargetChange = (v) => {
    setTargetWl(v);
    if (v && v === sourceWl) setSourceWl('');
    setCurrentPage(1);
  };

  const gapActive = sourceWl && targetWl && sourceWl !== targetWl;
  const activeFilterCount = countActiveFilters(filters) + (gapActive ? 1 : 0);

  // Export
  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    const filename = gapActive
      ? `device-coverage-gap_${tenantShortName}_${sourceWl}-not-in-${targetWl}_${today}.csv`
      : `device-comparison_${tenantShortName}_${today}.csv`;
    downloadBlob(rowsToCsv(filtered, visibleCols, { bpcLoaded }), filename);
  };

  // Pretty label for a workload key — used in source/target prose so it matches the dropdown.
  const workloadDisplay = (wl) => {
    if (wl === 'm365')             return 'Seen By M365';
    if (wl === 'entraJoined')      return 'Entra Joined';
    if (wl === 'entraRegistered')  return 'Entra Registered';
    return wl.toUpperCase();
  };

  // Workload selectability for source/target
  const workloadOption = (val, label) => {
    const disabled = val === 'bpc' && !bpcLoaded;
    return (
      <option key={val} value={val} disabled={disabled} title={disabled ? 'BPC not loaded — no mapping for this tenant' : undefined}>
        {label}{disabled ? ' (no mapping)' : ''}
      </option>
    );
  };

  const hasResults = rows.length > 0;
  const anyError = m365Error || drmmError || bpcError || entraError || intuneError;

  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Coverage Analysis</div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <ArrowsRightLeftIcon className="h-5 w-5 text-purple-400" />
          Device Inventory Comparison
        </h1>
      </div>

      {/* Filter bar */}
      <div className="card p-4">
        <div className="flex flex-wrap items-center gap-3">
          {/* Tenant — required */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Tenant</label>
            <select
              value={selectedTenant}
              onChange={(e) => setSelectedTenant(e.target.value)}
              disabled={tenantsLoading}
              className="input-field py-1 px-2 text-xs min-w-[220px]"
            >
              <option value="">{tenantsLoading ? 'Loading tenants…' : '— Select a tenant —'}</option>
              {tenants
                .map((t) => {
                  const shortName = t.ShortName;
                  const friendlyName = t.FriendlyName;
                  const label = shortName && friendlyName
                    ? `(${shortName}) ${friendlyName}`
                    : (shortName || friendlyName || t.Domain || t.TenantID);
                  return { id: t.TenantID, label };
                })
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            {tenantsLoading && (
              <span className="inline-block h-3 w-3 border-b-2 border-blue-400 rounded-full animate-spin flex-shrink-0" aria-label="Loading tenants" />
            )}
          </div>

          {/* Snapshot Date */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Snapshot Date</label>
            <input
              type="date"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
              className="input-field py-1 px-2 text-xs w-36"
            />
          </div>

          <button
            type="button"
            onClick={handleLoad}
            disabled={!selectedTenant || m365Loading || drmmLoading || bpcLoading || entraLoading || intuneLoading}
            className="btn-primary text-xs px-3 py-1.5"
          >
            {(m365Loading || drmmLoading || bpcLoading || entraLoading || intuneLoading) ? 'Loading…' : 'Load Devices'}
          </button>

          {/* 30-day staleness filter toggle — default on. When on, workloads with lastSeen
              > 30 days before snapshot are treated as missing (red dot, dropped from In-#
              count, dropped from Source/Target gap, "No" in CSV). When off, presence reflects
              raw inventory hits regardless of lastSeen age. */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Hide stale (&gt;30d)</label>
            <button
              type="button"
              onClick={() => { setStaleFilterEnabled((p) => !p); setCurrentPage(1); }}
              title={staleFilterEnabled
                ? 'On: workloads with lastSeen > 30 days before snapshot are treated as missing'
                : 'Off: every workload presence reflects raw inventory hits, regardless of lastSeen age'}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                staleFilterEnabled ? 'bg-blue-600' : 'bg-gray-700'
              }`}
            >
              <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                staleFilterEnabled ? 'translate-x-5' : 'translate-x-1'
              }`} />
            </button>
          </div>

          {/* Timezone picker — defaults to browser-detected local zone, persists globally
              (shared with the alerts/timeline pages via TZ_STORAGE_KEY). */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Time zone</label>
            <select
              value={selectedTz}
              onChange={(e) => setSelectedTz(e.target.value)}
              className="input-field py-1 px-2 text-xs"
              title="Display timezone for Last Seen and tooltips"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
              ))}
            </select>
          </div>
        </div>

        {/* Load times */}
        {(m365LoadTime || drmmLoadTime || bpcLoadTime || entraLoadTime || intuneLoadTime) && (
          <div className="flex flex-wrap gap-4 mt-2 pt-2 border-t border-gray-800/60">
            {m365LoadTime && (
              <span className="text-[11px] text-gray-600">
                Seen By M365: <span className="text-gray-500">{m365LoadTime}s</span>
                {m365Devices && <span className="text-gray-600"> · {m365Devices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {drmmLoadTime && (
              <span className="text-[11px] text-gray-600">
                DRMM: <span className="text-gray-500">{drmmLoadTime}s</span>
                {drmmDevices && <span className="text-gray-600"> · {drmmDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {entraLoadTime && (
              <span className="text-[11px] text-gray-600">
                Entra: <span className="text-gray-500">{entraLoadTime}s</span>
                {entraDevices && <span className="text-gray-600"> · {entraDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {intuneLoadTime && (
              <span className="text-[11px] text-gray-600">
                Intune: <span className="text-gray-500">{intuneLoadTime}s</span>
                {intuneDevices && <span className="text-gray-600"> · {intuneDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
            {bpcLoadTime && (
              <span className="text-[11px] text-gray-600">
                BPC: <span className="text-gray-500">{bpcLoadTime}s</span>
                {bpcDevices && <span className="text-gray-600"> · {bpcDevices.length.toLocaleString()} devices</span>}
              </span>
            )}
          </div>
        )}
      </div>

      {/* No-mapping banner — applies whenever BPC isn't loaded for the chosen tenant */}
      {hasResults && !bpcLoaded && (
        <div className="flex items-start gap-3 p-3 bg-amber-900/20 border border-amber-700/40 rounded-lg text-sm text-amber-200">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-400 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium">No BlackPoint Cyber tenant is mapped to this M365 tenant.</p>
            <p className="text-xs text-amber-300/80 mt-0.5">
              Showing M365 + DRMM only. To include BPC, map the tenant in{' '}
              <Link to="/admin/tenants/bpc" className="underline hover:text-amber-200">
                Admin → Tenants → BlackPoint Cyber MDR Tenants
              </Link>.
            </p>
          </div>
        </div>
      )}

      {/* Per-workload error banners */}
      {anyError && (
        <div className="space-y-2">
          {m365Error && (
            <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
              <span>"Seen By M365" workload failed to load: {m365Error}</span>
            </div>
          )}
          {drmmError && (
            <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
              <span>DRMM failed to load: {drmmError}</span>
            </div>
          )}
          {entraError && (
            <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
              <span>Entra failed to load: {entraError}</span>
            </div>
          )}
          {intuneError && (
            <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
              <span>Intune failed to load: {intuneError}</span>
            </div>
          )}
          {bpcError && (
            <div className="flex items-center gap-3 p-3 bg-red-900/30 border border-red-700/50 rounded-lg text-sm text-red-300">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-400" />
              <span>BPC failed to load: {bpcError}</span>
            </div>
          )}
        </div>
      )}

      {/* Results */}
      {hasResults && (
        <div className="card overflow-hidden">
          {/* Top toolbar — Source/Target gap filter + Export */}
          <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-gray-800 min-h-[40px]">
            <div className="flex items-center gap-1.5">
              <label className="text-xs text-gray-400">Source</label>
              <select value={sourceWl} onChange={(e) => onSourceChange(e.target.value)}
                className="input-field py-1 px-2 text-xs w-36">
                <option value="">(none)</option>
                {workloadOption('m365',             'Seen By M365')}
                {workloadOption('drmm',             'DRMM')}
                {workloadOption('entraRegistered',  'Entra Registered')}
                {workloadOption('entraJoined',      'Entra Joined')}
                {workloadOption('intune',           'Intune')}
                {workloadOption('bpc',              'BPC')}
              </select>
              <span className="text-xs text-gray-500">not in</span>
              <label className="text-xs text-gray-400">Target</label>
              <select value={targetWl} onChange={(e) => onTargetChange(e.target.value)}
                className="input-field py-1 px-2 text-xs w-36">
                <option value="">(none)</option>
                {workloadOption('m365',             'Seen By M365')}
                {workloadOption('drmm',             'DRMM')}
                {workloadOption('entraRegistered',  'Entra Registered')}
                {workloadOption('entraJoined',      'Entra Joined')}
                {workloadOption('intune',           'Intune')}
                {workloadOption('bpc',              'BPC')}
              </select>
            </div>

            <div className="flex-1 min-w-[8rem]">
              <span className="text-xs text-gray-500">
                {filtered.length} {filtered.length === 1 ? 'device' : 'devices'}
                {gapActive && <> in <span className="text-gray-300">{workloadDisplay(sourceWl)}</span> not in <span className="text-gray-300">{workloadDisplay(targetWl)}</span></>}
              </span>
              {staleFilterEnabled && (
                <div className="text-[10px] text-gray-600 mt-0.5">
                  Workloads where lastSeen &gt; {STALE_DAYS} days before snapshot are treated as missing. Toggle "Hide stale" off in the filter bar to disable.
                </div>
              )}
            </div>

            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5 transition-colors"
              >
                <XMarkIcon className="h-3 w-3" />
                Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
              </button>
            )}

            <ColumnPickerButton
              allCols={COMPARE_COLS}
              hiddenCols={hiddenCols}
              setHiddenCols={setHiddenCols}
              defaultHidden={DEFAULT_HIDDEN_COLS}
            />

            <button
              type="button"
              onClick={handleExport}
              disabled={filtered.length === 0}
              className="btn-secondary text-xs px-2.5 py-1 flex items-center gap-1.5"
            >
              <ArrowDownTrayIcon className="h-3.5 w-3.5" />
              Export CSV
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <CompareTable
              rows={paged}
              visibleCols={visibleCols}
              bpcLoaded={bpcLoaded}
              sortField={sortField} sortDir={sortDir} onSort={handleSort}
              filters={filters} onFilterChange={setFilter} distinct={distinct}
              selectedTz={selectedTz}
            />
          </div>

          {/* Pagination */}
          {filtered.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageSize={pageSize}
              totalItems={filtered.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
            />
          )}
        </div>
      )}

      {/* Empty state — before first load */}
      {!hasResults && !m365Loading && !drmmLoading && !bpcLoading && (
        <div className="card p-8 text-center text-sm text-gray-500">
          {selectedTenant
            ? 'Click "Load Devices" to compare device inventory across workloads.'
            : 'Select a tenant to begin.'}
        </div>
      )}
    </div>
  );
}

// ── Compare table ────────────────────────────────────────────────────────────
//
// Header + body are both driven by `visibleCols` (a TZ-suffixed, visibility-filtered
// view of COMPARE_COLS that the parent computes). Each column's `render(d, ctx)` and
// `cellClass` / `cellTitle` handle its own presentation, so the body is a plain
// `visibleCols.map(...)` instead of 11+ hardcoded <td>s.
function CompareTable({ rows, visibleCols, bpcLoaded, sortField, sortDir, onSort, filters, onFilterChange, distinct, selectedTz }) {
  const { colWidths, thRefs, tableRef, startResize } = useKeyedColWidths(visibleCols);
  // Format helper for last-seen / tooltip values in the user-selected tz.
  const fmt = (ts) => formatTimestampShort(ts, selectedTz);
  return (
    <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${colWidths.reduce((a, b) => a + b, 0)}px` }}>
      <TableHead cols={visibleCols} colWidths={colWidths} thRefs={thRefs} startResize={startResize}
        sortField={sortField} sortDir={sortDir} onSort={onSort}
        filters={filters} onFilterChange={onFilterChange} distinct={distinct} />
      <tbody>
        {rows.length === 0 ? (
          <tr><td colSpan={visibleCols.length} className="px-3 py-8 text-center text-gray-500">No devices match the current filters</td></tr>
        ) : rows.map((d) => {
          // Per-row context shared by every render function. Stale flags: device is in raw
          // inventory but the workload's last-seen falls outside the staleness cutoff —
          // tooltip says "stale (last seen …)" rather than "missing".
          const ctx = {
            bpcLoaded,
            fmt,
            m365Stale:   d.inventoryM365   && !d.hasM365,
            drmmStale:   d.inventoryDrmm   && !d.hasDrmm,
            entraStale:  d.inventoryEntra  && !d.hasEntra,
            intuneStale: d.inventoryIntune && !d.hasIntune,
            bpcStale:    d.inventoryBpc    && !d.hasBpc,
          };
          return (
            <tr key={d.name} className="border-b border-gray-800/50 hover:bg-gray-800/30">
              {visibleCols.map((col) => (
                <td
                  key={col.key}
                  className={col.cellClass || (col.align === 'center' ? 'px-3 py-2 text-center' : 'px-3 py-2')}
                  title={col.cellTitle ? col.cellTitle(d) : undefined}
                >
                  {col.render(d, ctx)}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
